import React, { useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { apiErr } from "../../../lib/apiErr";
import { isCanceled, useLatestRequest } from "../../../lib/useLatest";
import { MarginFilters } from "./MarginFilters";
import { MarginCards } from "./MarginCards";
import { CostRevenueChart, ProviderStackChart } from "./MarginCharts";
import { ProviderTable } from "./ProviderTable";
import { RowsTable } from "./RowsTable";
import { addDays, downloadCsv, fillSeries, rangeError, toParams, wibToday } from "./marginUtil";

const PAGE = 100;

export default function MarginReport() {
  const [f, setF] = useState(() => ({ from: addDays(wibToday(), -29), to: wibToday(), group: "day", provider: "", source: "" }));
  const [providers, setProviders] = useState([]); const [sum, setSum] = useState(null); const [rows, setRows] = useState(null); const [page, setPage] = useState(1);
  const [err, setErr] = useState(""); const [rowsErr, setRowsErr] = useState(""); const [exporting, setExporting] = useState("");
  const nextSum = useLatestRequest(); const nextRows = useLatestRequest();
  const bad = rangeError(f);
  const set = (p) => { setF((x) => ({ ...x, ...p })); setPage(1); };
  useEffect(() => { api.get("/platform/finance/ai-margin/providers").then((r) => setProviders(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (bad) return;
    setSum(null); setErr("");
    api.get("/platform/finance/ai-margin", { params: toParams(f), signal: nextSum() }).then((r) => setSum(r.data)).catch((e) => { if (!isCanceled(e)) setErr(apiErr(e, "Gagal memuat laporan margin")); });
  }, [f, bad, nextSum]);
  useEffect(() => {
    if (bad) return;
    setRows(null); setRowsErr("");
    api.get("/platform/finance/ai-margin/rows", { params: { ...toParams(f), page, page_size: PAGE }, signal: nextRows() }).then((r) => setRows(r.data)).catch((e) => { if (!isCanceled(e)) { setRows({ items: [], total_items: 0, total_pages: 0 }); setRowsErr(apiErr(e, "Gagal memuat rincian")); } });
  }, [f, page, bad, nextRows]);
  const series = useMemo(() => (sum ? fillSeries(sum.series, /^\d{4}-\d{2}-\d{2}$/.test(sum.from || "") && /^\d{4}-\d{2}-\d{2}$/.test(sum.to || "") ? { from: sum.from, to: sum.to, group: sum.group || f.group } : f) : []), [sum]); // eslint-disable-line react-hooks/exhaustive-deps
  const ph = (h) => (err ? <p className={`flex items-center justify-center text-sm text-slate-400 ${h}`} data-testid="margin-chart-unavailable">Data tidak tersedia.</p> : <div className={`animate-pulse rounded-2xl bg-slate-50 ${h}`} />);
  const exportCsv = async (kind) => {
    const name = `margin-ai_${f.from}_${f.to}_${f.group}${f.provider ? `_${f.provider}` : ""}${kind === "page" ? `_p${page}` : ""}.csv`;
    if (kind === "page") { downloadCsv(rows.items, name); return; }
    setExporting("all");
    try {
      const all = []; let p = 1, pages = 1;
      do { const r = await api.get("/platform/finance/ai-margin/rows", { params: { ...toParams(f), page: p, page_size: 500 } }); all.push(...(r.data.items || [])); pages = r.data.total_pages || 1; p += 1; } while (p <= pages);
      downloadCsv(all, name); toast.success(`${all.length} baris diekspor`);
    } catch (e) { toast.error(apiErr(e, "Gagal ekspor CSV")); } finally { setExporting(""); }
  };
  return (
    <div data-testid="margin-report">
      <MarginFilters f={f} set={set} providers={providers} />
      {(err || rowsErr) && <p className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700" data-testid="margin-error">{err || rowsErr}</p>}
      <div className={`transition-opacity ${bad ? "pointer-events-none opacity-40" : ""}`} data-testid="margin-content" data-stale={!!bad}>
        <MarginCards t={sum?.total || (err ? {} : null)} />
        <div className="mt-6 grid gap-6 2xl:grid-cols-2">
          <div className="aivora-card p-5"><p className="mb-3 text-sm font-bold text-slate-900">Biaya AI vs pendapatan bersih</p>{sum ? <CostRevenueChart data={series} /> : ph("h-72")}</div>
          <div className="aivora-card p-5"><p className="mb-3 text-sm font-bold text-slate-900">Biaya per provider</p>{sum ? <ProviderStackChart data={series} providers={sum.providers || []} /> : ph("h-64")}</div>
        </div>
        <h2 className="mb-3 mt-8 text-base font-bold text-slate-900 md:text-lg">Per provider</h2>
        <ProviderTable items={sum?.providers || []} total={sum?.total} selected={f.provider} onPick={(p) => set({ provider: p })} />
        <div className="mt-8"><RowsTable data={rows} page={page} setPage={setPage} onExport={exportCsv} exporting={exporting} /></div>
      </div>
      <p className="mt-4 flex items-start gap-2 text-[11px] text-slate-400" data-testid="margin-note"><Info size={12} className="mt-0.5 shrink-0" /> Semua nilai USD. Biaya riil dari usage provider; provider tanpa usage (TTS, STT, gambar, video, tool, avatar) memakai harga katalog tanpa margin. Pemakaian gratis tetap dicatat biayanya (margin bisa negatif). Hanya data sejak fitur aktif; histori &gt; 1 tahun dihapus otomatis.</p>
    </div>
  );
}
